import { executeRequest, type ApiResult } from '@/core/either';
import { http } from '@/core/api';
import type { AdditionalAccountClosureRequest, CloseAdditionalAccountResponse } from '../types/additionalAccountClosure.type';
import type {
  CreateAdditionalAccountRequest,
  CreateAdditionalAccountResponse,
} from '../types/createAdditionalAccount.type';
import type {
  AdditionalAccountAvailabilityItemResponse,
  AdditionalAccountAvailabilityParams,
} from '../types/additionalAccountAvailability.type';
import type {
  AdditionalAccountRenameRequest,
  AdditionalAccountRenameResponse,
} from '../types/renameAdditionalAccount.type';
import type { UserAccountsResponse } from '../types/userAccounts.type';
import type { CompanyAccountsCatalogResponse } from '../types/companyAccountsCatalog.type';
import type { AssignUserAccountsRequest } from '../types/assignUserAccounts.type';

/**
 * Contrato ML-41, verificado contra `AdditionalAccountsController.java` en
 * `master` de ms-wallet (PR 80441, mergeado) y `wallet-b2b.yaml` en `master`
 * de `ms-config-api-gateway` — ambos en producción del lado de gateway/BE.
 * `200` con `CloseAdditionalAccountResponse` en éxito. Reasons de error:
 * `ACCOUNT_NOT_FOUND`, `BAD_REQUEST`, `VAULT_HAS_BALANCE` (saldo != 0),
 * `ACCOUNT_IS_CLOSED`, `ACCOUNT_CURRENCY_MISMATCH` (cuenta destino de otra
 * moneda/empresa), `GENERIC_ERROR`. El `Claim-User-Id` que declara el
 * contrato lo inyecta el gateway desde el token: el front no lo manda.
 */
export const additionalAccountService = {
  closeAdditionalAccount(
    accountId: number,
    body: AdditionalAccountClosureRequest
  ): Promise<ApiResult<CloseAdditionalAccountResponse>> {
    return executeRequest(() =>
      http.post<CloseAdditionalAccountResponse>(`wallet-b2b/b2b/additional-accounts/${accountId}/closure`, body)
    );
  },

  /**
   * ML-40 — renombra una cuenta adicional. Contrato verificado contra
   * `AdditionalAccountsController.java` en `master` de ms-wallet
   * (`@PatchMapping("/{accountId}/name")`, body `UpdateAccountNameRequestDto`
   * con campo `name` en inglés) y `wallet-b2b.yaml` en `master` del gateway
   * — el `PATCH .../nombre` de la descripción del ticket era un diseño
   * abandonado de product-gateway (su PR de gateway quedó OPEN sin mergear).
   * `200` con `AccountNameResponseDto` (camelCase). Error de negocio:
   * `400 ACCOUNT_NAME_ALREADY_EXISTS` (case-insensitive, excluye la propia
   * cuenta). No editable si la cuenta está cerrada. El backend valida
   * `@Size(max = 100)`; el form ya limita a 50 (más estricto).
   * @param accountId - `wallet.walletId` de la cuenta adicional (mismo criterio que closure).
   * @param body - `{ name }` con el nombre nuevo ya trimmeado.
   * @returns Promesa con el resultado tipado usando Either (respuesta cruda sin adapter — ver el type).
   */
  renameAdditionalAccount(
    accountId: number,
    body: AdditionalAccountRenameRequest
  ): Promise<ApiResult<AdditionalAccountRenameResponse>> {
    return executeRequest(() =>
      http.patch<AdditionalAccountRenameResponse>(`wallet-b2b/b2b/additional-accounts/${accountId}/name`, body)
    );
  },

  /**
   * ML-3 — crea una cuenta adicional en una moneda donde la empresa ya tiene
   * cuenta principal activa. Contrato verificado contra
   * `AdditionalAccountsController.java` (`@PostMapping` sin path sobre
   * `/wallet-b2b/b2b/additional-accounts`) y `CreateAdditionalAccountRequest`
   * en `master` de ms-wallet, más una llamada real a CI. El body es camelCase
   * — el `{moneda, nombre, monto_inicial}` de la descripción del ticket nunca
   * existió en el código.
   *
   * NO manda headers `Claim-*` a propósito: el gateway los inyecta desde los
   * claims del token (`integration.request.header.Claim-User-Id:
   * context.authorizer.claims:custom:customer_id` en `wallet-b2b.yaml`), así
   * que cualquier valor del front se sobreescribe.
   *
   * `201` con `CreateAdditionalAccountResponse`. Reasons de error:
   * `ACCOUNT_ALREADY_EXISTS` (nombre duplicado entre las cuentas no cerradas
   * de la empresa), `CREATE_ACCOUNT_NOT_POSSIBLE` (empresa sin país, sin
   * cuenta principal en la moneda, o combinación país-moneda no habilitada),
   * `TRX_LIMIT_EXCEEDED` (límite de cuentas de la moneda alcanzado — el reason
   * está mal elegido del lado backend, hay un TODO en su código),
   * `BAD_REQUEST`, `GENERIC_ERROR`.
   *
   * OJO con `initialFunding` en la respuesta: el fondeo es asíncrono y su
   * fallo NO revierte la creación (`FAILED` = cuenta creada, plata sin mover).
   * @param body - `{ currencyCode, name, initialAmount?, userIds? }` — omitir `initialAmount` cuando no
   * hay fondeo, y `userIds` cuando no hay preseleccionados. `userIds` asigna en la misma llamada: no hace
   * falta `assignUsersToAdditionalAccount` después del `201`.
   * @returns Promesa con el resultado tipado usando Either (respuesta cruda camelCase, sin adapter).
   */
  createAdditionalAccount(
    body: CreateAdditionalAccountRequest
  ): Promise<ApiResult<CreateAdditionalAccountResponse>> {
    return executeRequest(() =>
      http.post<CreateAdditionalAccountResponse>('wallet-b2b/b2b/additional-accounts', body)
    );
  },

  /**
   * Configuración país-moneda que habilita las cuentas adicionales. Ambos
   * params son opcionales y el backend los combina con AND; sin ninguno
   * devuelve el catálogo global (que NO sirve para indexar por moneda: dos
   * países con la misma moneda colisionarían).
   *
   * A diferencia del resto del recurso, este endpoint NO lleva headers
   * `Claim-*`: el controller de ms-wallet no declara ninguno. No copiarle los
   * headers a los métodos vecinos.
   * @param params - `{ country?, currency? }` en ISO; el backend responde `country` en ISO2.
   * @returns Promesa con el resultado tipado usando Either (array crudo — adaptar con `getAdditionalAccountAvailabilityAdapter`).
   */
  getAdditionalAccountAvailability(
    params: AdditionalAccountAvailabilityParams
  ): Promise<ApiResult<AdditionalAccountAvailabilityItemResponse[]>> {
    return executeRequest(() =>
      http.get<AdditionalAccountAvailabilityItemResponse[]>(
        'wallet-b2b/b2b/additional-accounts/availability',
        { params }
      )
    );
  },

  /**
   * ML-66 — lista las cuentas (principales y adicionales) que tiene asignadas
   * un usuario del equipo. Contrato verificado contra
   * `UserAssignedAccountsResponse.java` / `UserAssignedAccountItemRecord.java`
   * en `master` de ms-wallet (merge commit `1fb85a8c4`). `200` con
   * `accounts: []` cuando el usuario no tiene cuentas asignadas — no es error.
   * @param userId - Id del usuario, el mismo que expone el listado de equipo.
   * @returns Promesa con el resultado tipado usando Either (respuesta cruda sin adapter — ver el type).
   */
  getUserAccounts(userId: number): Promise<ApiResult<UserAccountsResponse>> {
    return executeRequest(() =>
      // El contrato declara `Claim-Customer-Id` como header obligatorio, pero
      // el gateway lo inyecta desde los claims del token
      // (`custom:customer_id`): si el front lo manda, lo pisa. No agregar.
      http.get<UserAccountsResponse>(`wallet-b2b/b2b/additional-accounts/users/${userId}/accounts`)
    );
  },

  /**
   * Catálogo de las cuentas (principal y adicionales) que el usuario del token
   * puede operar, agrupadas por moneda: el backend ya filtra (un nivel 4 recibe
   * todas las de la empresa) y `isAssigned` dejó de existir. Alimenta el selector de cuentas
   * (ML-65) y el listado de adicionales (ML-118). Contrato verificado contra
   * `CurrencyAccountItemRecord.java` en `master` de ms-wallet (PR 86083): el
   * `/filtered` que lo precedía ya no existe. Serializa camelCase.
   *
   * El query `account-status` es opcional y su default backend ya es `ACTIVE`,
   * así que no se manda. NO manda headers `Claim-*`: el gateway los inyecta
   * desde los claims del token y cualquier valor del front se sobreescribe.
   * @returns Promesa con el resultado tipado usando Either (respuesta cruda sin adapter — ver el type).
   */
  getAccountsGroupedByCurrency(): Promise<ApiResult<CompanyAccountsCatalogResponse>> {
    return executeRequest(() =>
      http.get<CompanyAccountsCatalogResponse>('wallet-b2b/b2b/additional-accounts/grouped-by-currency')
    );
  },

  /**
   * ML-8 — le quita a un usuario el acceso a una cuenta adicional. Contrato
   * verificado contra `AdditionalAccountsApi.java` /
   * `AdditionalAccountsController.java` en `master` de ms-wallet
   * (`@DeleteMapping("/{accountId}/users/{userId}")`). Las rutas en español
   * de ML-8 (`/cuentasadicionales/...`) y de ML-68 (`/subcuentas/...`) se
   * contradicen entre sí y ninguna existe.
   *
   * Devuelve `void` con 200. Dos reglas del `@Operation.description` que los
   * tickets no mencionan:
   * - **falla si la cuenta no es adicional**: una cuenta principal no se
   *   puede revocar, porque su acceso no viene de una asignación. Por eso la
   *   UI no ofrece el botón en las principales.
   * - si la afiliación ya estaba inactiva, el request es un **no-op**: repetir
   *   el revocado no es un error.
   * @param accountId - Id de la cuenta adicional (el `id` que trae el catálogo).
   * @param userId - Id del usuario al que se le quita el acceso.
   * @returns Promesa con el resultado tipado usando Either (sin cuerpo en éxito).
   */
  unassignUserFromAccount(accountId: number, userId: number): Promise<ApiResult<void>> {
    return executeRequest(() =>
      // Mismo criterio: el `Claim-User-Id` del contrato lo pone el gateway.
      http.delete<void>(`wallet-b2b/b2b/additional-accounts/${accountId}/users/${userId}`)
    );
  },

  /**
   * ML-67/ML-8 — asigna una cuenta adicional a N usuarios. Contrato verificado
   * contra `AdditionalAccountsController.java` en `master` de ms-wallet: el eje
   * es **por cuenta**, no por usuario (`@PostMapping("/{accountId}/users")`,
   * body `AssignAdditionalAccountRequestDto` con `userIds: List<Long>`). De ahí
   * la firma: una llamada por cuenta, con todos sus usuarios en el body.
   *
   * Para el caso inverso —un usuario sobre N cuentas— es el llamador el que
   * itera las cuentas y decide qué hacer ante un fallo parcial; esta capa no
   * orquesta.
   *
   * El backend **agrega** afiliaciones, no reemplaza (`persistAffiliations` en
   * `AdditionalAccountsAffiliationServiceImpl`): mandar un subconjunto no le
   * quita el acceso a los demás usuarios de esa cuenta, y repetir una
   * asignación es idempotente.
   *
   * Devuelve `void` con 200, pero `filterEligibleUserIds` descarta usuarios no
   * elegibles **en silencio** del lado del backend: un 200 no garantiza que las
   * afiliaciones quedaron hechas, quien necesite certeza debe recargar el
   * listado.
   *
   * NO manda headers `Claim-*`: mismo criterio que el resto del recurso.
   * @param accountId - Id de la cuenta adicional (el `id` que trae el catálogo, o el `accountId` del 201 del create).
   * @param userIds - Ids numéricos de los usuarios a asignar; con la lista vacía no debe llamarse.
   * @returns Promesa con el resultado tipado usando Either (sin cuerpo en éxito).
   */
  assignUsersToAdditionalAccount(accountId: number, userIds: number[]): Promise<ApiResult<void>> {
    const body: AssignUserAccountsRequest = { userIds };
    return executeRequest(() =>
      http.post<void>(`wallet-b2b/b2b/additional-accounts/${accountId}/users`, body)
    );
  },
};
